import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { PropsWithChildren, useEffect, useState } from "react";

import EmptyState from "@/components/Core/EmptyState";
import { useTranslate } from "@/hooks";
import { hideBootShell } from "@/lib/bootShell";
import { setSiteMeta } from "@/lib/pageMeta";
import { mayHaveSession, restoreSession } from "@/lib/session";
import { getCheck } from "@/services";
import { dispatch, setBootstrap } from "@/store";
import { CheckData, CheckResponse } from "@/types";

import { HEALTH_CHECK_QUERY_KEY } from "./data";

/**
 * Title and description follow the backend (lib/pageMeta adds each page's own), but the tab
 * icon deliberately does not: the favicon stays the static `public/favicon-*.png` set declared
 * in index.html, matching the logo, which is also served straight from `public/`.
 */
const applySiteMeta = (data: CheckData) =>
  setSiteMeta({
    name: data.name,
    headText: data.headText,
    desc: data.desc,
    ogImage: data.ogImage,
    darkLogo: data.darkLogo,
    lightLogo: data.lightLogo,
  });

const fullPage = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

const HealthGate = ({ children }: PropsWithChildren) => {
  const t = useTranslate();
  const { data, isSuccess, isError, isFetching, refetch } = useQuery<CheckResponse>({
    queryKey: HEALTH_CHECK_QUERY_KEY,
    queryFn: getCheck,
  });

  useEffect(() => {
    if (!data) return;
    dispatch(setBootstrap(data));
    applySiteMeta(data.data);
  }, [data]);

  // The token is not kept across reloads; a player who was signed in waits for the cookie restore,
  // a guest renders at once and the restore finishes in the background.
  const [sessionReady, setSessionReady] = useState(() => !mayHaveSession());
  useEffect(() => {
    void restoreSession().finally(() => setSessionReady(true));
  }, []);

  useEffect(() => {
    if (isError) hideBootShell();
  }, [isError]);

  // The backend did not answer. This used to be the loading clouds for ever,
  // on every tab, with a hand reload the only way out.
  if (isError) {
    return (
      <Box component="main" sx={fullPage}>
        <EmptyState
          size="page"
          title="error.unreachable-title"
          description="error.unreachable-desc"
          action={
            <Stack alignItems="center" gap={1.5}>
              <Button
                variant="contained"
                onClick={() => void refetch()}
                disabled={isFetching}
                sx={{ borderRadius: 999, px: 3 }}
              >
                {t(isFetching ? "common.retrying" : "error.unreachable-retry")}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t("error.unreachable-footer")}
              </Typography>
            </Stack>
          }
        />
      </Box>
    );
  }

  // The boot skeleton from index.html is still up; the layouts take it down.
  if (!isSuccess || !sessionReady) return null;

  return <>{children}</>;
};

export default HealthGate;
